import React, { useEffect, useMemo } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import toast from "react-hot-toast";
import {
  loadTeacherOptions,
  NO_APLICA_OPTION,
  useCreateProfileType,
  useCreateTeacherRate,
  useProfileTypes,
  useUpdateTeacherRate,
} from "../hooks/useTeacherRates";
import { DateField } from "../../../components/DateField";

const EMPTY = {
  teacher: null,
  profile_type: NO_APLICA_OPTION,
  hourly_value: "",
  effective_from: "", // "YYYY-MM-DD" (contrato de DateField)
  is_active: true,
};

function TeacherRateForm({ show, onHide, editing }) {
  const [values, setValues] = React.useState(EMPTY);

  const createMutation = useCreateTeacherRate();
  const updateMutation = useUpdateTeacherRate();
  const mutation = editing ? updateMutation : createMutation;

  // Catálogo de tipos de perfil de la academia — el OWNER puede añadir uno
  // nuevo escribiéndolo en el selector (react-select "creatable").
  const { data: profileTypes = [] } = useProfileTypes();
  const createProfileType = useCreateProfileType();
  const profileTypeOptions = useMemo(
    () => [
      NO_APLICA_OPTION,
      ...profileTypes.map((t) => ({ value: t.id, label: t.name })),
    ],
    [profileTypes]
  );

  const handleCreateProfileType = (inputValue) => {
    const name = inputValue.trim();
    if (!name) return;
    createProfileType.mutate(name, {
      onSuccess: (created) => {
        setValues((v) => ({
          ...v,
          profile_type: { value: created.id, label: created.name },
        }));
        toast.success("Tipo de perfil creado");
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.field_errors?.name?.[0] ??
            err?.response?.data?.detail ??
            "No se pudo crear el tipo de perfil"
        ),
    });
  };

  useEffect(() => {
    if (editing) {
      setValues({
        teacher: editing.teacher
          ? {
              value: editing.teacher,
              label:
                editing.teacher_display ?? String(editing.teacher),
            }
          : null,
        profile_type: editing.profile_type
          ? {
              value: editing.profile_type,
              label: editing.profile_type_display ?? String(editing.profile_type),
            }
          : NO_APLICA_OPTION,
        hourly_value: editing.hourly_value ?? "",
        effective_from: editing.effective_from ?? "",
        is_active: editing.is_active ?? true,
      });
    } else {
      setValues(EMPTY);
    }
  }, [editing, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.teacher) {
      toast.error("Selecciona un docente");
      return;
    }
    if (!values.effective_from) {
      toast.error("Indica la fecha de vigencia");
      return;
    }
    const body = {
      teacher: values.teacher.value,
      profile_type: values.profile_type?.value ?? null,
      hourly_value: Number(values.hourly_value),
      effective_from: values.effective_from,
      is_active: values.is_active,
    };
    const payload = editing ? { id: editing.id, ...body } : body;
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(editing ? "Tarifa actualizada" : "Tarifa creada");
        onHide();
      },
      onError: () => toast.error("Error al guardar la tarifa"),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editing ? "Editar tarifa" : "Nueva tarifa docente"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="rate-teacher">
            <Form.Label>Docente</Form.Label>
            {/* AsyncSelect loads teachers from /api/v1/memberships/?role=TEACHER */}
            <AsyncSelect
              inputId="rate-teacher"
              cacheOptions
              defaultOptions
              loadOptions={loadTeacherOptions}
              value={values.teacher}
              onChange={(opt) => setValues((v) => ({ ...v, teacher: opt }))}
              placeholder="Buscar docente…"
              noOptionsMessage={() => "Sin resultados"}
              loadingMessage={() => "Cargando…"}
              styles={{
                control: (base) => ({ ...base, minHeight: 44 }),
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="rate-profile-type">
            <Form.Label>Tipo de perfil</Form.Label>
            {/* Tipos reales de la academia (/api/v1/profile-types/, por defecto
                Profesional/Practicante). El OWNER puede añadir uno nuevo
                escribiéndolo y eligiendo "Crear …". */}
            <CreatableSelect
              inputId="rate-profile-type"
              options={profileTypeOptions}
              value={values.profile_type}
              onChange={(opt) =>
                setValues((v) => ({
                  ...v,
                  profile_type: opt ?? NO_APLICA_OPTION,
                }))
              }
              onCreateOption={handleCreateProfileType}
              isDisabled={createProfileType.isPending}
              isLoading={createProfileType.isPending}
              formatCreateLabel={(input) => `Crear tipo de perfil "${input}"`}
              placeholder="Tipo de perfil…"
              noOptionsMessage={() => "Sin opciones"}
              isClearable
              styles={{
                control: (base) => ({ ...base, minHeight: 44 }),
              }}
            />
            <Form.Text muted>
              ¿Falta un perfil? Escríbelo y elige “Crear …”.
            </Form.Text>
          </Form.Group>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="rate-hourly-value">
                <Form.Label>Valor/hora (COP)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={values.hourly_value}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, hourly_value: e.target.value }))
                  }
                  placeholder="25000"
                  required
                  style={{ minHeight: 44 }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="rate-effective-from">
                <Form.Label>Vigente desde</Form.Label>
                <DateField
                  id="rate-effective-from"
                  value={values.effective_from}
                  onChange={(v) =>
                    setValues((prev) => ({ ...prev, effective_from: v }))
                  }
                  required
                />
                <Form.Text muted>
                  Un cambio no reescribe tarifas pasadas.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Check
            type="switch"
            id="rate-is-active"
            label="Activo"
            checked={Boolean(values.is_active)}
            onChange={(e) =>
              setValues((v) => ({ ...v, is_active: e.target.checked }))
            }
            className="mb-1"
            style={{ minHeight: 44 }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending}
            style={{ minHeight: 44 }}
          >
            {mutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : editing ? (
              "Guardar cambios"
            ) : (
              "Crear tarifa"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export { TeacherRateForm };
